<template>
	<section class="vcx-section">
		<div class="vcx-container">
			<div class="vcx-grid">
				<div class="vcx-col-12 vcx-col-6-lg vcx-col-offset-3-lg vcx-text-center-lg">
					<h2 class="vcx-h1 vcx-mb-100">Newsroom</h2>

					<p class="vcx-body-md vcx-body-lg-responsive vcx-text-gray-40">
						Insights, updates, and ideas from the Fundrise team
					</p>
				</div>
			</div>

			<div class="vcx-grid vcx-mt-300">
				<div class="vcx-col-12 vcx-col-10-xl vcx-col-offset-1-xl">
					<article class="vcx-newsroom-feature">
						<div class="vcx-newsroom-feature__video">
							<iframe
								src="https://www.youtube.com/embed/d8WL_qbqpT8"
								title="Fundrise Innovation Fund (VCX) | Investor Day 2026"
								allow="autoplay; encrypted-media; picture-in-picture; web-share"
								frameborder="0"
								referrerpolicy="strict-origin-when-cross-origin"
								allowfullscreen
								loading="lazy"
							></iframe>
						</div>
						<div class="vcx-newsroom-feature__content">
							<span class="vcx-label-sm vcx-text-tertiary">In case you missed it</span>
							<h3 class="vcx-h4 vcx-my-100">Watch the first Innovation Fund Investor Day</h3>
							<p class="vcx-body-md vcx-text-gray-40">
								Watch the full recording as our leadership team lays out the vision for our Public
								Venture Capital (PVC) fund.
							</p>
						</div>
					</article>
				</div>
			</div>

			<VcxNewsCarousel
				v-if="articles.length || isVcxSanityLoading"
				title="Latest news"
				description="Recent updates from the Fundrise team about VCX"
				blogCategorySlug="vcx"
			>
				<template #scroll-items>
					<VcxNewsArticleCard
						v-for="(article, index) in articles"
						:article="article"
						:key="article.slug.current"
					/>
				</template>
			</VcxNewsCarousel>

			<VcxNewsCarousel
				v-if="podcasts.length || isVcxSanityLoading"
				title="Onward podcast"
				description="Onward is a Fundrise podcast offering unique insights and perspectives from company co-founder and CEO Ben Miller and guests."
				blogCategorySlug="onward"
			>
				<template #scroll-items>
					<VcxPodcastArticleCard v-for="podcast in podcasts" :key="podcast.title" :podcast="podcast" />
				</template>
			</VcxNewsCarousel>
		</div>
	</section>
</template>

<script setup lang="ts">
import { inject, ref } from 'vue';
import VcxNewsArticleCard from '@components/vcx/vcx-news-article-card.vue';
import VcxNewsCarousel from '@components/vcx/vcx-news-carousel.vue';
import VcxPodcastArticleCard from '@components/vcx/vcx-podcast-article-card.vue';
import { SanityFeaturedPodcast, VcxArticleTeaser } from 'types/sanity/vcx-landing-page';

interface Props {
	articles?: Array<VcxArticleTeaser>;
	podcasts?: Array<SanityFeaturedPodcast>;
}

withDefaults(defineProps<Props>(), {
	articles: () => [],
	podcasts: () => []
});

const isVcxSanityLoading = inject('isVcxSanityLoading', ref(false));
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive' as *;

.vcx-newsroom-feature {
	display: flex;
	border-radius: var(--vcx-radius-100);
	border: 1px solid var(--vcx-gray-70);
	padding: var(--vcx-space-200);
	gap: var(--vcx-space-300);

	@include vcx-media-smo {
		flex-direction: column;
	}

	@include vcx-media-md {
		align-items: center;

		&__content,
		&__video {
			width: 50%;
		}
	}

	&__video iframe {
		border-radius: var(--vcx-radius-100);
		width: 100%;
		aspect-ratio: 16 / 9;
	}
}
</style>
